/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

const vehicleProgramsTitle = "Vehicle Programs";
const BASE_VEHICLE_PROGRAMS_ENDPOINT = '/HttpTriggerVehiclePrograms'
const VEHICLE_PROGRAMS_ITEM_DETAILS_ROUTE = '/sensingsystemlayout';

enum RequestOperations {
  UPDATE = 'update',
  GET = 'get',
}

enum RequestTypes {
  UPDATE_SENSOR_POSITIONS = 'updateSensorPositions',
  GET_MORE_INFO_DOWNLOAD = 'getMoreInfoDownload',
  GET_PERFORMANCE_TABLE = 'getPerformanceTable',
  GET_REPORT_OPTIONS = 'getReportOptions',
  GET_VIOLATIONS_REPORT = 'getViolationsReport',
  GET_REPORT_METADATA = 'getReportMetadata',
  GET_BLUE_LINE_STATUS = 'getBlueLineStatus',
  GET_VEHICLE_PROGRAMS_FOR_LANDING = 'getVehicleProgramsForLanding',
  ADD_VEHICLE_PROGRAM = 'addVehicleProgram',
  EDIT_VEHICLE_PROGRAM = 'editVehicleProgram',
  SHOULD_DELETE_VEHICLE_PROGRAM = 'shouldDeleteVehicleProgram',
  DELETE_VEHICLE_PROGRAM = 'deleteVehicleProgram',
  UPDATE_ATTENUATION = 'updateAttenuation',
  UPDATE_PERFORMANCE_TABLE = 'updatePerformanceTable',
  SYNC_UNSYNC_REPORT = 'syncUnsyncReport',

  GET_VEHICLE_PROGRAM_ITEM_DETAILS_DATA = 'getVehicleProgramItemDetailsData',
  GET_VEHICLE_PROGRAM_TREE = 'getVehicleProgramTree',

  ADD_DEVELOPMENT_STAGE_DATA = 'addDevelopmentStageData',
  GET_DEVELOPMENT_STAGE_DATA = 'getDevelopmentStageData',
  DELETE_DEVELOPMENT_STAGE_DATA = 'deleteDevelopmentStage',
  RENAME_DEVELOPMENT_STAGE_DATA = 'renameDevelopmentStageData',

  GET_ADAS_PROGRAM_DATA = 'getAdasProgramData',

  GET_INFO_TO_DISPLAY_BEFORE_GENERATING_BLUELINE = 'getInfoToDisplayBeforeGeneratingBlueline',

  ADD_ADAS_PROGRAM_TO_DEV_STAGE = 'addAdasProgramToDevStage',
  REVIEW_ADAS_SENSORS = 'reviewAdasSensors',

  FETCH_REPORT_GENERATION_STATUS_OBJECT = 'fetchReportGenerationStatusObject',

  GET_DEV_STAGE_ID_TO_DATA = 'getDevStageIdToData',
}

enum RequestKeys {
  REQUEST_OPERATION = 'request_operation',
  REQUEST_TYPE = 'request_type',
}

const formRequestUrl = (operation: RequestOperations, type: RequestTypes) => {
  return `${BASE_VEHICLE_PROGRAMS_ENDPOINT}?${RequestKeys.REQUEST_OPERATION}=${operation}&${RequestKeys.REQUEST_TYPE}=${type}`;
}
const VEHICLE_PROGRAMS_CONSTANTS = {
  landing: {
    title: vehicleProgramsTitle,
    endpoints: {
      getVehiclePrograms: formRequestUrl(RequestOperations.GET, RequestTypes.GET_VEHICLE_PROGRAMS_FOR_LANDING),
      addVehicleProgram: formRequestUrl(RequestOperations.UPDATE, RequestTypes.ADD_VEHICLE_PROGRAM),
      editVehicleProgram: formRequestUrl(RequestOperations.UPDATE, RequestTypes.EDIT_VEHICLE_PROGRAM),
      deleteVehicleProgram: formRequestUrl(RequestOperations.UPDATE, RequestTypes.DELETE_VEHICLE_PROGRAM),
      shouldDeleteVehicleProgram: formRequestUrl(RequestOperations.UPDATE, RequestTypes.SHOULD_DELETE_VEHICLE_PROGRAM),
    }
  },
  itemDetails: {
    route: VEHICLE_PROGRAMS_ITEM_DETAILS_ROUTE,
    constructRouteUrl: (vehicleProgramId: string) => `${VEHICLE_PROGRAMS_CONSTANTS.itemDetails.route}?vehicleProgramId=${vehicleProgramId}`,
    sensingSystemPerformance: {
      viewReportRoute: `${VEHICLE_PROGRAMS_ITEM_DETAILS_ROUTE}-view-report`,
      fetchReportGenerationStatusObject: formRequestUrl(RequestOperations.GET, RequestTypes.FETCH_REPORT_GENERATION_STATUS_OBJECT),
    },
    endpoints: {
      devStageIdToData: formRequestUrl(RequestOperations.GET, RequestTypes.GET_DEV_STAGE_ID_TO_DATA),

      getVehicleProgramTree: formRequestUrl(RequestOperations.GET, RequestTypes.GET_VEHICLE_PROGRAM_TREE),
      getVehicleProgramItemDetailsData: formRequestUrl(RequestOperations.GET, RequestTypes.GET_VEHICLE_PROGRAM_ITEM_DETAILS_DATA),

      addDevelopmentStageData: formRequestUrl(RequestOperations.UPDATE, RequestTypes.ADD_DEVELOPMENT_STAGE_DATA),
      getDevelopmentStageData: formRequestUrl(RequestOperations.GET, RequestTypes.GET_DEVELOPMENT_STAGE_DATA),
      deleteDevelopmentStageData: formRequestUrl(RequestOperations.UPDATE, RequestTypes.DELETE_DEVELOPMENT_STAGE_DATA),
      renameDevelopmentStageData: formRequestUrl(RequestOperations.UPDATE, RequestTypes.RENAME_DEVELOPMENT_STAGE_DATA),

      getAdasProgramData: formRequestUrl(RequestOperations.GET, RequestTypes.GET_ADAS_PROGRAM_DATA),
      downloadVehicleProgramCsvFile:'/HttpTriggerDownloadFile?request=downloadVehicleProgramCsvFile',
      downloadDevelopmentStageFile: '/HttpTriggerDownloadFile?request=downloadVehicleProgramDevStage',
      updateAttenuation: formRequestUrl(RequestOperations.UPDATE, RequestTypes.UPDATE_ATTENUATION),
      updateSensorPositions: formRequestUrl(RequestOperations.UPDATE, RequestTypes.UPDATE_SENSOR_POSITIONS),
      getMoreInfoDownload: formRequestUrl(RequestOperations.GET, RequestTypes.GET_MORE_INFO_DOWNLOAD),
      generateBlueLine: '/orchestrators/DurableFunctionsBluelineOrchestrator',
      getPerformanceTable: formRequestUrl(RequestOperations.GET, RequestTypes.GET_PERFORMANCE_TABLE),
      getReportOptions: formRequestUrl(RequestOperations.GET, RequestTypes.GET_REPORT_OPTIONS),
      getViolationsReport: formRequestUrl(RequestOperations.GET, RequestTypes.GET_VIOLATIONS_REPORT),
      getReportMetadata: formRequestUrl(RequestOperations.GET, RequestTypes.GET_REPORT_METADATA),
      getBlueLineStatus: formRequestUrl(RequestOperations.GET, RequestTypes.GET_BLUE_LINE_STATUS),
      updatePerformanceTable: formRequestUrl(RequestOperations.UPDATE, RequestTypes.UPDATE_PERFORMANCE_TABLE),
      syncUnsyncReport: formRequestUrl(RequestOperations.UPDATE, RequestTypes.SYNC_UNSYNC_REPORT),
      getInfoToDisplayBeforeGeneratingBlueline: formRequestUrl(RequestOperations.GET, RequestTypes.GET_INFO_TO_DISPLAY_BEFORE_GENERATING_BLUELINE),

      addAdasProgramToDevStage: formRequestUrl(RequestOperations.GET, RequestTypes.ADD_ADAS_PROGRAM_TO_DEV_STAGE),
      reviewAdasSensors: formRequestUrl(RequestOperations.GET, RequestTypes.REVIEW_ADAS_SENSORS),
    }
  },
  toast: {
    TOAST_ERROR_DURATION: 5000
  }
}
export const deleteVehicleDevStageTempFileEndpoint = "/HttpTriggerSensors?req=delete&type=vehicle-temp-file"
export {vehicleProgramsTitle, VEHICLE_PROGRAMS_CONSTANTS};